/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { BigNumber } from '@ethersproject/bignumber'
import { ISwapResponse, ITokenInfoInResponse } from '@swapnet-xyz/sdk'
import { asSupportedChain } from 'constants/chains'
import { RPC_PROVIDERS } from 'constants/providers'
import { isAvalanche, isBsc, isMatic, nativeOnChain } from 'constants/tokens'
import { MixedRouteSDK } from 'eth-mainnet-few-router-sdk-3'
import { ChainId, Currency, CurrencyAmount, Token, TradeType } from 'eth-mainnet-few-sdk-core-2'
import { getFewTokenFromOriginalToken, Pair as FewPair, Pair, Route as V2Route } from 'eth-mainnet-few-v2-sdk-3'
import { FeeAmount, Pool, Route as V3Route } from 'eth-mainnet-few-v3-sdk-2'
import { AlphaRouter } from 'eth-mainnet-smart-order-router-4'
import { DutchOrderInfo, DutchOrderInfoJSON } from 'eth-mainnet-uniswapx-sdk-2'
import JSBI from 'jsbi'
import { toSlippagePercent } from 'utils/slippage'

import { getApproveInfo, getWrapInfo } from './gas'
import {
  ClassicQuoteData,
  ClassicTrade,
  DutchOrderTrade,
  GetQuoteArgs,
  InterfaceTrade,
  isClassicQuoteResponse,
  isRingXQuoteResponse,
  PoolType,
  QuoteMethod,
  QuoteState,
  RingXTrade,
  RouterPreference,
  SwapRouterNativeAssets,
  TradeFillType,
  TradeResult,
  URAQuoteResponse,
  URAQuoteType,
  V2PoolInRoute,
  V3PoolInRoute,
} from './types'

interface RouteResult {
  routev3: V3Route<Currency, Currency> | null
  routev2: V2Route<Currency, Currency> | null
  // routeFew: FewRoute<Currency, Currency> | null
  mixedRoute: MixedRouteSDK<Currency, Currency> | null
  inputAmount: CurrencyAmount<Currency>
  outputAmount: CurrencyAmount<Currency>
}

const routers = new Map<ChainId, AlphaRouter>()
export function getRouter(chainId: ChainId): AlphaRouter {
  const router = routers.get(chainId)
  if (router) return router

  const supportedChainId = asSupportedChain(chainId)
  if (supportedChainId) {
    const provider = RPC_PROVIDERS[supportedChainId]
    const router = new AlphaRouter({ chainId, provider })
    routers.set(chainId, router)
    return router
  }

  throw new Error(`Router does not support this chain (chainId: ${chainId}).`)
}

/**
 * Transforms a Routing API quote into an array of routes that can be used to
 * create a `Trade`.
 */
export function computeRoutes(
  currencyIn: Currency,
  currencyOut: Currency,
  routes: ClassicQuoteData['route']
): RouteResult[] | undefined {
  if (routes.length === 0) return []

  const tokenIn = routes[0]?.[0]?.tokenIn
  const tokenOut = routes[0]?.[routes[0]?.length - 1]?.tokenOut
  if (!tokenIn || !tokenOut) throw new Error('Expected both tokenIn and tokenOut to be present')

  try {
    return routes.map((route) => {
      if (route.length === 0) {
        throw new Error('Expected route to have at least one pair or pool')
      }
      const rawAmountIn = route[0].amountIn
      const rawAmountOut = route[route.length - 1].amountOut

      if (!rawAmountIn || !rawAmountOut) {
        throw new Error('Expected both amountIn and amountOut to be present')
      }

      const isOnlyV2 = isVersionedRoute<V2PoolInRoute>(PoolType.V2Pool, route)
      const isOnlyV3 = isVersionedRoute<V3PoolInRoute>(PoolType.V3Pool, route)

      // const fewCurrencyIn = getFewTokenFromOriginalToken(currencyIn.wrapped, currencyIn.chainId)
      // const fewCurrencyOut = getFewTokenFromOriginalToken(currencyOut.wrapped, currencyIn.chainId)

      return {
        routev3: isOnlyV3 ? new V3Route(route.map(parsePool), currencyIn, currencyOut) : null,
        routev2: isOnlyV2 ? new V2Route(route.map(parseFewPair), currencyIn, currencyOut) : null,
        mixedRoute:
          !isOnlyV3 && !isOnlyV2 ? new MixedRouteSDK(route.map(parsePoolOrPair), currencyIn, currencyOut) : null,
        inputAmount: CurrencyAmount.fromRawAmount(currencyIn, rawAmountIn),
        outputAmount: CurrencyAmount.fromRawAmount(currencyOut, rawAmountOut),
      }
    })
  } catch (e) {
    console.error('Error computing routes', e)
    return
  }
}

function computeRingXRoutes(
  currencyIn: Currency,
  currencyOut: Currency,
  swapInfo: ISwapResponse
): RouteResult[] | undefined {
  const tokenIn = swapInfo.tokens.find((i: ITokenInfoInResponse) => i.referenceId == swapInfo.sell.referenceId)
  const tokenOut = swapInfo.tokens.find((i: ITokenInfoInResponse) => i.referenceId == swapInfo.buy.referenceId)
  if (!tokenIn || !tokenOut) throw new Error('Expected both tokenIn and tokenOut to be present')

  try {
    if (swapInfo.routes.length === 0) {
      throw new Error('Expected route to have at least one pair or pool')
    }
    const rawAmountIn = swapInfo.sell.amount
    const rawAmountOut = swapInfo.buy.amount

    if (!rawAmountIn || !rawAmountOut) {
      throw new Error('Expected both amountIn and amountOut to be present')
    }

    const isOnlyV2 = true

    const fewCurrencyIn = getFewTokenFromOriginalToken(currencyIn.wrapped, currencyIn.chainId)
    const fewCurrencyOut = getFewTokenFromOriginalToken(currencyOut.wrapped, currencyIn.chainId)
    const fewCurrencyAmountIn = CurrencyAmount.fromRawAmount(fewCurrencyIn, JSBI.BigInt(rawAmountIn))
    const fewCurrencyAmountOut = CurrencyAmount.fromRawAmount(fewCurrencyOut, JSBI.BigInt(rawAmountOut))

    const ringXroute = {
      routev3: null,
      routev2: isOnlyV2
        ? new V2Route([new Pair(fewCurrencyAmountIn, fewCurrencyAmountOut)], currencyIn, currencyOut)
        : null,
      mixedRoute: null,
      inputAmount: CurrencyAmount.fromRawAmount(currencyIn, rawAmountIn),
      outputAmount: CurrencyAmount.fromRawAmount(currencyOut, rawAmountOut),
    }
    return [ringXroute]
  } catch (e) {
    console.error('Error computing routes', e)
    return
  }
}

const parsePoolOrPair = (pool: V3PoolInRoute | V2PoolInRoute): Pool | FewPair => {
  return pool.type === PoolType.V3Pool ? parsePool(pool) : parseFewPair(pool)
}

// eslint-disable-next-line import/no-unused-modules
export function isVersionedRoute<T extends V2PoolInRoute | V3PoolInRoute>(
  type: T['type'],
  route: (V3PoolInRoute | V2PoolInRoute)[]
): route is T[] {
  return route.every((pool) => pool.type === type)
}

function toDutchOrderInfo(orderInfoJSON: DutchOrderInfoJSON): DutchOrderInfo {
  const { nonce, input, outputs, exclusivityOverrideBps } = orderInfoJSON
  return {
    ...orderInfoJSON,
    nonce: BigNumber.from(nonce),
    exclusivityOverrideBps: BigNumber.from(exclusivityOverrideBps),
    input: {
      ...input,
      startAmount: BigNumber.from(input.startAmount),
      endAmount: BigNumber.from(input.endAmount),
    },
    outputs: outputs.map((output) => ({
      ...output,
      startAmount: BigNumber.from(output.startAmount),
      endAmount: BigNumber.from(output.endAmount),
    })),
  }
}

// Prepares the currencies used for the actual Swap (either UniswapX or Universal Router)
// May not match `currencyIn` that the user selected because for ETH inputs in UniswapX, the actual
// swap will use WETH.
function getTradeCurrencies(args: GetQuoteArgs, isUniswapXTrade: boolean): [Currency, Currency] {
  const {
    tokenInAddress,
    tokenInChainId,
    tokenInDecimals,
    tokenInSymbol,
    tokenOutAddress,
    tokenOutChainId,
    tokenOutDecimals,
    tokenOutSymbol,
  } = args

  const tokenInIsNative = Object.values(SwapRouterNativeAssets).includes(tokenInAddress as SwapRouterNativeAssets)
  const tokenOutIsNative = Object.values(SwapRouterNativeAssets).includes(tokenOutAddress as SwapRouterNativeAssets)

  const currencyIn = tokenInIsNative
    ? nativeOnChain(tokenInChainId)
    : parseToken({ address: tokenInAddress, chainId: tokenInChainId, decimals: tokenInDecimals, symbol: tokenInSymbol })
  const currencyOut = tokenOutIsNative
    ? nativeOnChain(tokenOutChainId)
    : parseToken({
        address: tokenOutAddress,
        chainId: tokenOutChainId,
        decimals: tokenOutDecimals,
        symbol: tokenOutSymbol,
      })

  if (!isUniswapXTrade) {
    return [currencyIn, currencyOut]
  }

  return [currencyIn.isNative ? currencyIn.wrapped : currencyIn, currencyOut]
}

function getClassicTradeDetails(
  currencyIn: Currency,
  currencyOut: Currency,
  data: URAQuoteResponse
): {
  gasUseEstimate?: number
  gasUseEstimateUSD?: number
  blockNumber?: string
  routes?: RouteResult[]
} {
  const classicQuote =
    data.routing === URAQuoteType.CLASSIC ? data.quote : data.allQuotes.find(isClassicQuoteResponse)?.quote
  return {
    gasUseEstimate: classicQuote?.gasUseEstimate ? parseFloat(classicQuote.gasUseEstimate) : undefined,
    gasUseEstimateUSD: classicQuote?.gasUseEstimateUSD ? parseFloat(classicQuote.gasUseEstimateUSD) : undefined,
    blockNumber: classicQuote?.blockNumber,
    routes: classicQuote ? computeRoutes(currencyIn, currencyOut, classicQuote.route) : undefined,
  }
}

function getRingXTradeDetails(
  currencyIn: Currency,
  currencyOut: Currency,
  data: URAQuoteResponse
): {
  gasUseEstimate?: number
  gasUseEstimateUSD?: number
  blockNumber?: string
  routes?: RouteResult[]
  swapnetInfo?: ISwapResponse
} {
  const ringXQuote = data.routing === URAQuoteType.RINGX ? data.quote : data.allQuotes.find(isRingXQuoteResponse)?.quote
  return {
    gasUseEstimate: ringXQuote?.gasUseEstimate ? parseFloat(ringXQuote.gasUseEstimate) : undefined,
    gasUseEstimateUSD: ringXQuote?.gasUseEstimateUSD ? parseFloat(ringXQuote.gasUseEstimateUSD) : undefined,
    routes: ringXQuote?.swapnetInfo ? computeRingXRoutes(currencyIn, currencyOut, ringXQuote.swapnetInfo) : undefined,
    blockNumber: ringXQuote?.blockNumber,
    swapnetInfo: ringXQuote?.swapnetInfo,
  }
}

export async function transformRoutesToTrade(
  args: GetQuoteArgs,
  data: URAQuoteResponse,
  quoteMethod: QuoteMethod
): Promise<TradeResult> {
  const { tradeType, needsWrapIfUniswapX, routerPreference, account, amount } = args

  // During the opt-in period, only return UniswapX quotes if the user has turned on the setting,
  // even if it is the better quote.
  const showUniswapXTrade = data.routing === URAQuoteType.DUTCH_LIMIT && routerPreference === RouterPreference.X

  const [currencyIn, currencyOut] = getTradeCurrencies(args, showUniswapXTrade)
  const { gasUseEstimateUSD, blockNumber, routes, gasUseEstimate } = getClassicTradeDetails(
    currencyIn,
    currencyOut,
    data
  )

  // If the top-level URA quote type is DUTCH_LIMIT, then UniswapX is better for the user
  const isUniswapXBetter = data.routing === URAQuoteType.DUTCH_LIMIT

  // Some sus javascript float math but it's ok because its just an estimate for display purposes
  const usdCostPerGas = gasUseEstimateUSD && gasUseEstimate ? gasUseEstimateUSD / gasUseEstimate : undefined

  const approveInfo = await getApproveInfo(account, currencyIn, amount, usdCostPerGas)

  // classicTrade
  const classicTrade = new ClassicTrade({
    v2Routes:
      routes
        ?.filter((r): r is RouteResult & { routev2: NonNullable<RouteResult['routev2']> } => r.routev2 !== null)
        .map(({ routev2, inputAmount, outputAmount }) => ({
          routev2,
          inputAmount,
          outputAmount,
        })) ?? [],
    // fewRoutes:
    //   routes
    //     ?.filter((r): r is RouteResult & { routeFew: NonNullable<RouteResult['routeFew']> } => r.routeFew !== null)
    //     .map(({ routeFew, inputAmount, outputAmount }) => ({
    //       routeFew,
    //       inputAmount,
    //       outputAmount,
    //     })) ?? [],
    // ringRoutes:
    //   routes
    //     ?.filter((r): r is RouteResult & { routeRing: NonNullable<RouteResult['routeRing']> } => r.routeRing !== null)
    //     .map(({ routeRing, inputAmount, outputAmount }) => ({
    //       routeRing,
    //       inputAmount,
    //       outputAmount,
    //     })) ?? [],
    v3Routes:
      routes
        ?.filter((r): r is RouteResult & { routev3: NonNullable<RouteResult['routev3']> } => r.routev3 !== null)
        .map(({ routev3, inputAmount, outputAmount }) => ({
          routev3,
          inputAmount,
          outputAmount,
        })) ?? [],
    mixedRoutes:
      routes
        ?.filter(
          (r): r is RouteResult & { mixedRoute: NonNullable<RouteResult['mixedRoute']> } => r.mixedRoute !== null
        )
        .map(({ mixedRoute, inputAmount, outputAmount }) => ({
          mixedRoute,
          inputAmount,
          outputAmount,
        })) ?? [],
    tradeType,
    gasUseEstimateUSD,
    approveInfo,
    blockNumber,
    isUniswapXBetter,
    requestId: data.quote.requestId,
    quoteMethod,
    inputTax: args.inputTax,
    outputTax: args.outputTax,
  })

  // During the opt-in period, only return UniswapX quotes if the user has turned on the setting,
  // even if it is the better quote.
  if (isUniswapXBetter && args.routerPreference === RouterPreference.X) {
    const orderInfo = toDutchOrderInfo(data.quote.orderInfo)
    const wrapInfo = await getWrapInfo(needsWrapIfUniswapX, account, currencyIn.chainId, amount, usdCostPerGas)

    const uniswapXTrade = new DutchOrderTrade({
      currencyIn,
      currenciesOut: [currencyOut],
      orderInfo,
      tradeType,
      quoteId: data.quote.quoteId,
      requestId: data.quote.requestId,
      classicGasUseEstimateUSD: classicTrade.totalGasUseEstimateUSD,
      wrapInfo,
      approveInfo,
      auctionPeriodSecs: data.quote.auctionPeriodSecs,
      deadlineBufferSecs: data.quote.deadlineBufferSecs,
      slippageTolerance: toSlippagePercent(data.quote.slippageTolerance),
    })

    return {
      state: QuoteState.SUCCESS,
      trade: uniswapXTrade,
    }
  }

  return { state: QuoteState.SUCCESS, trade: classicTrade }
}

export function transformRingXQuoteResponse(data: ISwapResponse, chainId: ChainId): URAQuoteResponse {
  const inputInfo = data.tokens.find((i: ITokenInfoInResponse) => i.referenceId == data.sell.referenceId)!
  const outputInfo = data.tokens.find((i: ITokenInfoInResponse) => i.referenceId == data.buy.referenceId)!
  const inputToken = new Token(chainId, inputInfo.address, inputInfo.decimals, inputInfo.address, inputInfo.name)
  const outputToken = new Token(chainId, outputInfo.address, outputInfo.decimals, outputInfo.address, outputInfo.name)
  const inputAmout = CurrencyAmount.fromRawAmount(inputToken, JSBI.BigInt(data.sell.amount))
  const outputAmout = CurrencyAmount.fromRawAmount(outputToken, JSBI.BigInt(data.buy.amount))

  return {
    routing: URAQuoteType.RINGX,
    quote: {
      amount: data.sell.amount,
      amountDecimals: inputAmout.toExact(),
      quote: data.buy.amount,
      quoteDecimals: outputAmout.toExact(),
      quoteGasAdjusted: '20000000000000000000',
      quoteGasAdjustedDecimals: '20.000000000000000000',
      gasUseEstimateQuote: '9600000000000000',
      gasUseEstimateQuoteDecimals: '0.009600000000000000',
      gasUseEstimate: '150000',
      gasUseEstimateUSD: '0.009600000000000000',
      gasPriceWei: '20000000',
      methodParameters: { calldata: 'string', value: 'string' },
      route: [],
      routeString: 'string',
      swapnetInfo: data,
    },
    allQuotes: [],
  }
}

export async function transformRingXRoutesToTrade(
  args: GetQuoteArgs,
  data: URAQuoteResponse,
  quoteMethod: QuoteMethod
): Promise<TradeResult> {
  const { tradeType, account, amount } = args

  // During the opt-in period, only return UniswapX quotes if the user has turned on the setting,
  // even if it is the better quote.
  // const showRingXTrade = data.routing === URAQuoteType.RINGX && routerPreference === RouterPreference.R

  const [currencyIn, currencyOut] = getTradeCurrencies(args, false)
  const { gasUseEstimateUSD, blockNumber, routes, gasUseEstimate, swapnetInfo } = getRingXTradeDetails(
    currencyIn,
    currencyOut,
    data
  )

  // Some sus javascript float math but it's ok because its just an estimate for display purposes
  const usdCostPerGas = gasUseEstimateUSD && gasUseEstimate ? gasUseEstimateUSD / gasUseEstimate : undefined

  const approveInfo = await getApproveInfo(account, currencyIn, amount, usdCostPerGas)

  // ringXTrade
  try {
    const ringXTrade = new RingXTrade({
      swapnetInfo: swapnetInfo as ISwapResponse,
      v2Routes:
        routes
          ?.filter((r): r is RouteResult & { routev2: NonNullable<RouteResult['routev2']> } => r.routev2 !== null)
          .map(({ routev2, inputAmount, outputAmount }) => ({
            routev2,
            inputAmount,
            outputAmount,
          })) ?? [],
      v3Routes: [],
      mixedRoutes: [],
      tradeType,
      gasUseEstimateUSD,
      approveInfo,
      blockNumber,
      quoteMethod,
      inputTax: args.inputTax,
      outputTax: args.outputTax,
    })

    return { state: QuoteState.SUCCESS, trade: ringXTrade }
  } catch (e) {
    console.log('err:', e)
  }
  return { state: QuoteState.NOT_FOUND, trade: undefined }
}

function parseToken({ address, chainId, decimals, symbol }: ClassicQuoteData['route'][0][0]['tokenIn']): Token {
  return new Token(chainId, address, parseInt(decimals.toString()), symbol)
}

// eslint-disable-next-line import/no-unused-modules
export function parsePool({ fee, sqrtRatioX96, liquidity, tickCurrent, tokenIn, tokenOut }: V3PoolInRoute): Pool {
  return new Pool(
    parseToken(tokenIn),
    parseToken(tokenOut),
    parseInt(fee) as FeeAmount,
    sqrtRatioX96,
    liquidity,
    parseInt(tickCurrent)
  )
}

// const parseFewPairOrRingPair = (pool: V3PoolInRoute | FewPoolInRoute | RingPoolInRoute): FewPair | RingPair | Pool => {
//   return pool.type === PoolType.V3Pool
//     ? parsePool(pool)
//     : pool.type === PoolType.RingPool
//     ? parseRingPair(pool)
//     : parseFewPair(pool)
// }

// const parseFewPairOrRingPair = (pool: V3PoolInRoute | FewPoolInRoute | RingPoolInRoute): FewPair | V2Pair | RingPair | Pool => {
//   return pool.type === PoolType.V3Pool ? parsePool(pool) : pool.type === PoolType.RingPool ? parseRingPair(pool) : parseV2Pair(pool)
// }

const parseFewPair = ({ reserve0, reserve1 }: V2PoolInRoute): FewPair =>
  new FewPair(
    CurrencyAmount.fromRawAmount(parseToken(reserve0.token), reserve0.quotient),
    CurrencyAmount.fromRawAmount(parseToken(reserve1.token), reserve1.quotient)
  )

// const parseV2Pair = ({ reserve0, reserve1 }: V2PoolInRoute): V2Pair =>
//   new V2Pair(
//     CurrencyAmount.fromRawAmount(parseToken(reserve0.token), reserve0.quotient),
//     CurrencyAmount.fromRawAmount(parseToken(reserve1.token), reserve1.quotient)
//   )

// const parseRingPair = ({ reserve0, reserve1 }: RingPoolInRoute): RingPair =>
//   new RingPair(
//     CurrencyAmount.fromRawAmount(parseToken(reserve0.token), reserve0.quotient),
//     CurrencyAmount.fromRawAmount(parseToken(reserve1.token), reserve1.quotient)
//   )
// TODO(WEB-2050): Convert other instances of tradeType comparison to use this utility function
export function isExactInput(tradeType: TradeType): boolean {
  return tradeType === TradeType.EXACT_INPUT
}

export function currencyAddressForSwapQuote(currency: Currency): string {
  if (currency.isNative) {
    if (isMatic(currency.chainId)) return SwapRouterNativeAssets.MATIC
    if (isBsc(currency.chainId)) return SwapRouterNativeAssets.BNB
    if (isAvalanche(currency.chainId)) return SwapRouterNativeAssets.AVAX
    return SwapRouterNativeAssets.ETH
  }

  return currency.address
}

export function isClassicTrade(trade?: InterfaceTrade): trade is ClassicTrade {
  return trade?.fillType === TradeFillType.Classic
}

export function isRingXTrade(trade?: InterfaceTrade): trade is RingXTrade {
  return trade?.fillType === TradeFillType.RingX
}

export function isUniswapXTrade(trade?: InterfaceTrade): trade is DutchOrderTrade {
  return trade?.fillType === TradeFillType.UniswapX
}

export function getTransactionCount(trade: InterfaceTrade): number {
  let count = 0
  if (trade.approveInfo.needsApprove) {
    count++ // approval step, which can happen in both classic and uniswapx
  }
  if (isUniswapXTrade(trade)) {
    if (trade.wrapInfo.needsWrap) {
      count++ // wrapping step for uniswapx
    }
  } else {
    count++ // classic onchain swap
  }
  return count
}
